import {ActionIcon, Badge, Button, Group, Paper, Stack, Text, Title, Tooltip} from '@mantine/core'
import {IconEdit, IconPlus} from '@tabler/icons-react'
import {type ReactNode, useState} from 'react'

import {isAdmin} from 'src/auth/is-admin'
import {useCurrentUser} from 'src/auth/use-current-user'

import {type ContactFieldErrors, ContactFormFields, validateContact} from './contact-form-fields'
import {type ContactWriteInput, createEmptyContact, type CustomerContact} from './schemas'
import {
    useCreateCustomerContact,
    useDeleteCustomerContact,
    useUpdateCustomerContact,
} from './use-customers'

type CustomerContactsSectionProps = {
    customerId: string
    contacts: CustomerContact[]
}

type EditingContactId = string | 'new' | null

function contactToDraft(contact: CustomerContact): ContactWriteInput {
    return {
        name: contact.name,
        title: contact.title ?? '',
        description: contact.description ?? '',
        phone: contact.phone ?? '',
        email: contact.email ?? '',
    }
}

type ContactReadContentProps = {
    contact: CustomerContact
    nameActions?: ReactNode
}

function ContactReadContent({contact, nameActions}: ContactReadContentProps) {
    return (
        <Stack gap="xs">
            <Group gap="xs" align="center" wrap="nowrap">
                <Title order={4} size="h4" style={{minWidth: 0}}>
                    {contact.name}
                </Title>
                {nameActions}
                {contact.isAssigned ? (
                    <Tooltip label="Assigned to a quote, order, or job; remove the assignment before deleting">
                        <Badge size="xs" variant="light" color="grape">
                            Assigned
                        </Badge>
                    </Tooltip>
                ) : null}
            </Group>
            {contact.title ? (
                <Text size="sm" c="dimmed">
                    {contact.title}
                </Text>
            ) : null}
            {contact.description ? (
                <Text size="sm" style={{whiteSpace: 'pre-wrap'}}>
                    {contact.description}
                </Text>
            ) : null}
            {contact.phone ? <Text size="sm">{contact.phone}</Text> : null}
            {contact.email ? <Text size="sm">{contact.email}</Text> : null}
        </Stack>
    )
}

export function CustomerContactsSection({customerId, contacts}: CustomerContactsSectionProps) {
    const user = useCurrentUser()
    const canEdit = isAdmin(user)
    const canDelete = isAdmin(user)
    const canAdd = user != null
    const createContact = useCreateCustomerContact(customerId)
    const updateContact = useUpdateCustomerContact(customerId)
    const deleteContact = useDeleteCustomerContact(customerId)

    const [editingContactId, setEditingContactId] = useState<EditingContactId>(null)
    const [draft, setDraft] = useState<ContactWriteInput>(createEmptyContact())
    const [errorMessage, setErrorMessage] = useState<string | null>(null)
    const [fieldErrors, setFieldErrors] = useState<ContactFieldErrors | null>(null)

    const saving = createContact.isPending || updateContact.isPending || deleteContact.isPending
    const editingContact =
        editingContactId != null && editingContactId !== 'new'
            ? contacts.find((contact) => contact.id === editingContactId)
            : undefined
    const editingContactIsAssigned = editingContact?.isAssigned ?? false
    const canDeleteWhileEditing =
        canDelete &&
        (contacts.length > 1 || editingContactId === 'new') &&
        !editingContactIsAssigned

    function startEditing(contact: CustomerContact) {
        setEditingContactId(contact.id)
        setDraft(contactToDraft(contact))
        setErrorMessage(null)
        setFieldErrors(null)
    }

    function startAddingContact() {
        setEditingContactId('new')
        setDraft(createEmptyContact())
        setErrorMessage(null)
        setFieldErrors(null)
    }

    function cancelEditing() {
        setEditingContactId(null)
        setDraft(createEmptyContact())
        setErrorMessage(null)
        setFieldErrors(null)
    }

    function updateDraft(partial: Partial<ContactWriteInput>) {
        setDraft((current) => ({...current, ...partial}))
    }

    async function handleSave() {
        const validation = validateContact(draft)
        if (validation.nameError || validation.emailError) {
            setFieldErrors(validation)
            setErrorMessage('Fix contact errors before saving')
            return
        }

        setFieldErrors(null)
        setErrorMessage(null)
        try {
            if (editingContactId === 'new') {
                await createContact.mutateAsync(draft)
            } else if (editingContactId != null) {
                await updateContact.mutateAsync({contactId: editingContactId, input: draft})
            }
            cancelEditing()
        } catch (error) {
            setErrorMessage(error instanceof Error ? error.message : 'Failed to save contact')
        }
    }

    async function handleDelete() {
        if (editingContactId === 'new') {
            cancelEditing()
            return
        }

        if (editingContactId == null) {
            return
        }

        if (!window.confirm('Remove this contact?')) {
            return
        }

        setErrorMessage(null)
        try {
            await deleteContact.mutateAsync(editingContactId)
            cancelEditing()
        } catch (error) {
            setErrorMessage(error instanceof Error ? error.message : 'Failed to delete contact')
        }
    }

    return (
        <Paper withBorder p="md" radius="md">
            <Stack gap="md">
                <Group justify="space-between" align="center">
                    <Title order={3}>Contacts</Title>
                    {canAdd && editingContactId !== 'new' ? (
                        <Tooltip
                            label="Save or cancel the contact you are editing first"
                            disabled={editingContactId == null}
                        >
                            <Button
                                leftSection={<IconPlus size={16} />}
                                variant="light"
                                size="compact-sm"
                                // data-disabled keeps pointer events for the tooltip
                                data-disabled={editingContactId != null || undefined}
                                aria-disabled={editingContactId != null}
                                onClick={(event) => {
                                    if (editingContactId != null) {
                                        event.preventDefault()
                                        return
                                    }
                                    startAddingContact()
                                }}
                            >
                                Add contact
                            </Button>
                        </Tooltip>
                    ) : null}
                </Group>

                {errorMessage ? (
                    <Text size="sm" c="red">
                        {errorMessage}
                    </Text>
                ) : null}

                <Stack gap="md">
                    {contacts.map((contact) =>
                        editingContactId === contact.id ? (
                            <Stack key={contact.id} gap="sm">
                                <ContactFormFields
                                    draft={draft}
                                    errors={fieldErrors ?? undefined}
                                    onUpdate={updateDraft}
                                />
                                <Group justify="flex-end" gap="sm">
                                    {canDeleteWhileEditing ? (
                                        <Button
                                            variant="subtle"
                                            color="red"
                                            size="compact-sm"
                                            onClick={() => void handleDelete()}
                                            disabled={saving}
                                        >
                                            Delete
                                        </Button>
                                    ) : null}
                                    <Button
                                        variant="default"
                                        size="compact-sm"
                                        onClick={cancelEditing}
                                        disabled={saving}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        size="compact-sm"
                                        onClick={() => void handleSave()}
                                        loading={saving}
                                    >
                                        Save
                                    </Button>
                                </Group>
                            </Stack>
                        ) : (
                            <ContactReadContent
                                key={contact.id}
                                contact={contact}
                                nameActions={
                                    canEdit && editingContactId == null ? (
                                        <ActionIcon
                                            size="sm"
                                            variant="default"
                                            aria-label={`Edit ${contact.name}`}
                                            onClick={() => startEditing(contact)}
                                            disabled={saving}
                                        >
                                            <IconEdit size={16} />
                                        </ActionIcon>
                                    ) : null
                                }
                            />
                        ),
                    )}

                    {canAdd && editingContactId === 'new' ? (
                        <Stack gap="sm">
                            <ContactFormFields
                                draft={draft}
                                errors={fieldErrors ?? undefined}
                                onUpdate={updateDraft}
                            />
                            <Group justify="flex-end" gap="sm">
                                {canDeleteWhileEditing ? (
                                    <Button
                                        variant="subtle"
                                        color="red"
                                        size="compact-sm"
                                        onClick={() => void handleDelete()}
                                        disabled={saving}
                                    >
                                        Delete
                                    </Button>
                                ) : null}
                                <Button
                                    variant="default"
                                    size="compact-sm"
                                    onClick={cancelEditing}
                                    disabled={saving}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    size="compact-sm"
                                    onClick={() => void handleSave()}
                                    loading={saving}
                                >
                                    Save
                                </Button>
                            </Group>
                        </Stack>
                    ) : null}

                    {contacts.length === 0 && editingContactId !== 'new' ? (
                        <Text size="sm" c="dimmed">
                            No contacts yet.
                        </Text>
                    ) : null}
                </Stack>
            </Stack>
        </Paper>
    )
}
